import * as React from 'react';
import cx from 'classnames';
import {
  FocusTrap,
  getTranslateValuesFromElement,
  Resizer,
  useMergedRefs,
  useLayoutEffect,
  Box,
  ShadowRoot,
} from '../../utils/index.js';
import { useDialogContext } from './DialogContext.js';
import { Transition } from 'react-transition-group';
import { DialogDragContext } from './DialogDragContext.js';
import { useDragAndDrop } from '../../utils/hooks/useDragAndDrop.js';
export const DialogMain = React.forwardRef((props, ref) => {
  let dialogContext = useDialogContext();
  let {
    className,
    children,
    styleType = 'default',
    isOpen = dialogContext.isOpen,
    isDismissible = dialogContext.isDismissible,
    onClose = dialogContext.onClose,
    closeOnEsc = dialogContext.closeOnEsc,
    trapFocus = dialogContext.trapFocus,
    setFocus = dialogContext.setFocus,
    preventDocumentScroll = dialogContext.preventDocumentScroll,
    onKeyDown,
    isDraggable = dialogContext.isDraggable,
    isResizable = dialogContext.isResizable,
    style: propStyle,
    placement = dialogContext.placement,
    ...rest
  } = props;
  let [style, setStyle] = React.useState();
  let dialogRef = React.useRef(null);
  let hasBeenResized = React.useRef(false);
  let previousFocusedElement = React.useRef();
  let originalBodyOverflow = React.useRef('');
  React.useEffect(() => {
    if (isOpen) originalBodyOverflow.current = document.body.style.overflow;
  }, [isOpen]);
  React.useEffect(() => {
    let ownerDocument = dialogRef.current?.ownerDocument;
    if (
      !ownerDocument ||
      !preventDocumentScroll ||
      'hidden' === originalBodyOverflow.current
    )
      return;
    if (isOpen) ownerDocument.body.style.overflow = 'hidden';
    else ownerDocument.body.style.overflow = originalBodyOverflow.current;
    return () => {
      ownerDocument.body.style.overflow = originalBodyOverflow.current;
    };
  }, [isOpen, preventDocumentScroll]);
  let handleKeyDown = (event) => {
    if (event.altKey) return;
    event.persist();
    if (isDismissible && closeOnEsc && 'Escape' === event.key && onClose)
      onClose(event);
    onKeyDown?.(event);
  };
  let { onPointerDown, transform } = useDragAndDrop(
    dialogRef,
    dialogContext.dialogRootRef,
    isDraggable,
  );
  let handlePointerDown = React.useCallback(
    (event) => {
      if (isDraggable) onPointerDown(event);
    },
    [isDraggable, onPointerDown],
  );
  useLayoutEffect(() => {
    if (!isDraggable || !isOpen) return;
    let [translateX, translateY] = getTranslateValuesFromElement(
      dialogRef.current,
    );
    setStyle((oldStyle) => ({
      ...oldStyle,
      insetInlineStart: dialogRef.current?.offsetLeft,
      insetBlockStart: dialogRef.current?.offsetTop,
      transform: `translate(${translateX}px,${translateY}px)`,
    }));
  }, [isDraggable, isOpen]);
  let setResizeStyle = React.useCallback((newStyle) => {
    setStyle((oldStyle) => ({
      ...oldStyle,
      ...newStyle,
    }));
  }, []);
  let content = React.createElement(
    Box,
    {
      className: cx(
        'iui-dialog',
        {
          'iui-dialog-default': 'default' === styleType,
          'iui-dialog-full-page': 'fullPage' === styleType,
          'iui-dialog-visible': isOpen,
          'iui-dialog-draggable': isDraggable,
        },
        className,
      ),
      role: 'dialog',
      ref: useMergedRefs(dialogRef, ref),
      onKeyDown: handleKeyDown,
      tabIndex: -1,
      'data-iui-placement': placement,
      style: {
        transform,
        ...style,
        ...propStyle,
      },
      ...rest,
    },
    React.createElement(
      ShadowRoot,
      null,
      React.createElement('slot', null),
      isResizable &&
        React.createElement(Resizer, {
          elementRef: dialogRef,
          containerRef: dialogContext.dialogRootRef,
          onResizeStart: () => {
            if (!hasBeenResized.current) {
              hasBeenResized.current = true;
              setResizeStyle({
                maxInlineSize: '100%',
              });
            }
          },
          onResizeEnd: setResizeStyle,
        }),
    ),
    children,
  );
  return React.createElement(
    Transition,
    {
      in: isOpen,
      appear: true,
      timeout: {
        exit: 600,
      },
      onEntered: () => {
        previousFocusedElement.current =
          dialogRef.current?.ownerDocument.activeElement;
        setFocus &&
          dialogRef.current?.focus({
            preventScroll: true,
          });
      },
      onExit: () => {
        if (
          dialogRef.current?.contains(
            dialogRef.current?.ownerDocument.activeElement,
          )
        )
          previousFocusedElement.current?.focus();
      },
      unmountOnExit: true,
      nodeRef: dialogRef,
    },
    React.createElement(
      DialogDragContext.Provider,
      {
        value: {
          onPointerDown: handlePointerDown,
        },
      },
      trapFocus && React.createElement(FocusTrap, null, content),
      !trapFocus && content,
    ),
  );
});
DialogMain.displayName = 'Dialog.Main';
